import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getSuperAdminHeader } from './SuperAdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Building2, Users, Server, AlertTriangle, FileText, ArrowLeft, Wifi, WifiOff, Activity, FolderTree, PhoneCall, Tags } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_STYLES = {
  0: 'bg-slate-500 text-white', 1: 'bg-blue-500 text-white', 2: 'bg-yellow-500 text-white',
  3: 'bg-orange-500 text-white', 4: 'bg-red-500 text-white', 5: 'bg-red-700 text-white',
};

function MetricBox({ label, value, icon: Icon, variant }) {
  const colors = {
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    green: 'border-green-200 bg-green-50 text-green-700',
    red: 'border-red-200 bg-red-50 text-red-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    gray: 'border-gray-200 bg-gray-50 text-gray-700',
  };
  return (
    <div className={`border rounded-lg p-3 ${colors[variant] || colors.gray}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4" />
        <span className="text-xs uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

export function SuperAdminTenantDetailPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [incidents, setIncidents] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [metricsRes, incidentsRes] = await Promise.all([
          axios.get(`${API}/super/tenants/${tenantId}/metrics`, { headers: getSuperAdminHeader() }),
          axios.get(`${API}/super/tenants/${tenantId}/incidents`, { headers: getSuperAdminHeader() }),
        ]);
        setMetrics(metricsRes.data);
        setIncidents(incidentsRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tenantId]);

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-[400px] w-full" /></div>;
  }

  const m = metrics?.metrics || {};
  const tenant = metrics?.tenant || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tenant-super/tenants')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h2 className="text-xl font-bold">{tenant.company_name || 'Tenant'}</h2>
          <p className="text-sm text-gray-500">ID: {tenantId}</p>
        </div>
        <Badge variant={tenant.is_active !== false ? 'default' : 'secondary'} className="ml-2">
          {tenant.is_active !== false ? 'Ativo' : 'Inativo'}
        </Badge>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <MetricBox label="Usuários" value={m.users_active || 0} icon={Users} variant="blue" />
        <MetricBox label="Hosts Zabbix" value={m.zabbix_hosts || 0} icon={Server} variant="green" />
        <MetricBox label="Alertas Ativos" value={m.zabbix_active_problems || 0} icon={AlertTriangle} variant="red" />
        <MetricBox label="Relatórios" value={m.reports_total || 0} icon={FileText} variant="gray" />
        <MetricBox label="Grupos" value={m.groups_total || 0} icon={FolderTree} variant="amber" />
      </div>

      {/* Zabbix Status */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4 flex items-center gap-4">
          {m.zabbix_status === 'connected' ? (
            <Badge className="bg-green-500 text-white"><Wifi className="h-3 w-3 mr-1" />Zabbix Conectado</Badge>
          ) : m.zabbix_status === 'not_configured' ? (
            <Badge variant="secondary"><WifiOff className="h-3 w-3 mr-1" />Zabbix Não Configurado</Badge>
          ) : (
            <Badge className="bg-red-500 text-white"><WifiOff className="h-3 w-3 mr-1" />Zabbix Erro</Badge>
          )}
          {m.zabbix_url && <span className="text-sm text-gray-500 font-mono">{m.zabbix_url}</span>}
        </CardContent>
      </Card>

      <Tabs defaultValue="incidents">
        <TabsList>
          <TabsTrigger value="incidents">Incidentes Ativos ({incidents?.total || 0})</TabsTrigger>
          <TabsTrigger value="users">Usuários ({m.users_total || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="incidents" className="mt-3">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-0">
              {!incidents?.connected ? (
                <div className="p-8 text-center text-gray-400">Zabbix não conectado</div>
              ) : incidents.incidents.length === 0 ? (
                <div className="p-8 text-center text-gray-400">Nenhum incidente ativo</div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-24">Severidade</TableHead>
                        <TableHead>Problema</TableHead>
                        <TableHead>Host</TableHead>
                        <TableHead className="w-28">Duração</TableHead>
                        <TableHead className="w-20">ACK</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {incidents.incidents.map((inc) => (
                        <TableRow key={inc.id}>
                          <TableCell>
                            <Badge className={`text-xs ${SEVERITY_STYLES[inc.severity]}`}>{inc.severity_label}</Badge>
                          </TableCell>
                          <TableCell className="text-sm font-medium">{inc.name}</TableCell>
                          <TableCell className="text-sm text-gray-500">{inc.host_name}</TableCell>
                          <TableCell className="text-sm text-gray-500">{inc.duration}</TableCell>
                          <TableCell>{inc.acknowledged ? <Badge className="bg-green-500 text-white text-xs">Sim</Badge> : <Badge variant="outline" className="text-xs">Não</Badge>}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-3">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(metrics?.users || []).map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.full_name}</TableCell>
                      <TableCell className="text-sm text-gray-500">{u.email}</TableCell>
                      <TableCell>
                        <Badge variant={u.is_active ? 'default' : 'secondary'} className={u.is_active ? 'bg-green-500' : ''}>
                          {u.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
